/**
 * Rules for the Add bin form. Pure, so the same checks the server enforces can be tested
 * without rendering. The server stays the authority; these only spare a round trip.
 */
import type { StorageLocation } from '../types';

/** Mirrors BIN_CODE_MAX_LENGTH on the server. */
export const BIN_CODE_MAX_LENGTH = 50;

export interface AddBinForm {
  locationId: string;
  code: string;
}

export type AddBinErrors = Partial<Record<keyof AddBinForm, string>>;

/** "Bin 1" and "bin 1" are the same bin to anyone reading the page, so compare without case. */
function sameCode(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * The bin number a user most likely wants next: "Bin 4" after "Bin 1".."Bin 3". Only offered
 * when every code in the location follows the "Bin N" pattern, so a location named in some
 * other way gets an empty field rather than a wrong guess.
 */
export function suggestBinCode(location: Pick<StorageLocation, 'bins'> | undefined): string {
  if (!location || location.bins.length === 0) return 'Bin 1';
  let highest = 0;
  for (const bin of location.bins) {
    const match = /^bin\s+(\d+)$/i.exec(bin.code.trim());
    if (!match) return '';
    highest = Math.max(highest, Number(match[1]));
  }
  return `Bin ${highest + 1}`;
}

export function validateAddBin(form: AddBinForm, locations: Pick<StorageLocation, 'id' | 'name' | 'bins'>[]): AddBinErrors {
  const errors: AddBinErrors = {};
  const location = locations.find((candidate) => candidate.id === form.locationId);
  if (!location) errors.locationId = 'Choose a location';

  const code = form.code.trim();
  if (!code) {
    errors.code = 'Enter a bin code';
  } else if (code.length > BIN_CODE_MAX_LENGTH) {
    errors.code = `Use ${BIN_CODE_MAX_LENGTH} characters or fewer`;
  } else if (location && location.bins.some((bin) => sameCode(bin.code, code))) {
    errors.code = `${location.name} already has a bin called "${code}"`;
  }
  return errors;
}
